/**
 * **One settings area's read, draft and save** (W28, FR-65, plan D86, D88).
 *
 * Every settings page edits a slice of the one settings document
 * (`GET /api/v1/settings`) on the page, and saves it with the bar:
 *
 * - **The read** is the service's answer — effective, overrides, defaults,
 *   version and the floor table — read when the page opens.
 * - **The draft** is the page's slice (`from(settings)`), changed in place;
 *   `dirty` compares it with the slice of what was read.
 * - **Save** sends **one** `PATCH` with the `version` read and only this
 *   page's keys (`toPatch(draft)`). A stale version (`version_conflict`) is
 *   said in the bar and the settings are read again — **the draft is kept**, so
 *   the next Save names the new version. Any other refusal is said in the bar
 *   too, the raw refusal kept in `refusal` for a page that says it at a field
 *   (`below_floor`, at its retention rule).
 * - **Discard** puts the draft back to what was read.
 *
 * `AreaWaiting` is a page's body until its slice is ready — the failure said
 * with a way to try again, else a skeleton — and `SETTINGS_BACK` its way back
 * to the hub, so every page says them one way.
 */
import { ApiError, belowFloorOf, errorText, useLoad, type RetentionRule, type SettingsAnswer, type SettingsPatch } from '@fiveox/prodoc-ui'
import { Alert, Button, Skeleton, SkeletonRegion } from '@fiveox/ui'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { useSettingsHost } from './host'

export const VERSION_CONFLICT = 'Someone changed these settings since you opened them. They have been read again — check them and save once more.'

/** Every page's way back: the hub. */
export const SETTINGS_BACK = { to: '/settings', label: 'Settings' }

/**
 * **The organisation's own retention rules** — the effective list less each
 * shipped type's every-app rule at its floor, which the service ships for every
 * type and which means *the law's minimum alone* (the service's
 * `org_retention_rule`, plan D46). A rule for one app, a rule above the floor
 * and a rule for a type of the organisation's own are its own. Sending only
 * these is the same answer: the service merges them over the shipped ones, and
 * a shipped type the list leaves out keeps its floor.
 */
export function orgRetentionRules(settings: SettingsAnswer): RetentionRule[] {
  const shipped = new Set((settings.defaults.retention?.rules ?? []).filter((r) => !r.owner_app).map((r) => `${r.document_type}:${r.months}`))
  return (settings.effective.retention?.rules ?? []).filter((r) => r.owner_app || !shipped.has(`${r.document_type}:${r.months}`))
}

export interface SettingsArea<D> {
  /** What the service answered; `null` while it is read. */
  settings: ReturnType<typeof useLoad<SettingsAnswer>>
  draft: D | null
  setDraft: (next: D) => void
  dirty: boolean
  saving: boolean
  /** What the bar says after a refused Save — `null` when nothing was refused. */
  problem: string | null
  /** The refusal itself, for a page that says it at a field. */
  refusal: unknown
  save: () => Promise<boolean>
  discard: () => void
}

export interface SettingsAreaOptions<D> {
  /** The page's slice of the settings document. */
  from: (settings: SettingsAnswer) => D
  /** The keys this page sends — never another page's. */
  toPatch: (draft: D) => Omit<SettingsPatch, 'version'>
  /** Whether two slices are the same; JSON equality by default. */
  same?: (a: D, b: D) => boolean
}

const jsonSame = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b)

export function useSettingsArea<D>({ from, toPatch, same = jsonSame }: SettingsAreaOptions<D>): SettingsArea<D> {
  const { client } = useSettingsHost()
  const settings = useLoad(() => client.settings.get(), [client])
  const [draft, setDraft] = useState<D | null>(null)
  const [saving, setSaving] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const [refusal, setRefusal] = useState<unknown>(null)
  const s = settings.data

  // The first read fills the draft; a later read (after a conflict) leaves a changed draft as it is.
  useEffect(() => {
    if (s) setDraft((current) => current ?? from(s))
    // `from` is the page's pure slice; only a new read matters here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s])

  const base = useMemo(() => (s ? from(s) : null), [s, from])
  const dirty = draft !== null && base !== null && !same(draft, base)

  const save = useCallback(async () => {
    if (!s || draft === null) return false
    setSaving(true)
    setProblem(null)
    setRefusal(null)
    try {
      const next = await client.settings.update({ version: s.version, ...toPatch(draft) })
      // A PATCH's answer need not repeat the floor table; keep the one read.
      const kept = next.floors ? next : { ...next, floors: s.floors }
      settings.set(kept)
      setDraft(from(kept))
      return true
    } catch (e) {
      setRefusal(e)
      if (e instanceof ApiError && e.reason === 'version_conflict') {
        setProblem(VERSION_CONFLICT)
        void settings.reload()
      } else if (belowFloorOf(e)) {
        setProblem('A rule is below the law’s minimum — see the rule marked above.')
      } else {
        setProblem(errorText(e))
      }
      return false
    } finally {
      setSaving(false)
    }
  }, [client, draft, from, s, settings, toPatch])

  const discard = useCallback(() => {
    if (s) setDraft(from(s))
    setProblem(null)
    setRefusal(null)
  }, [from, s])

  return { settings, draft, setDraft, dirty, saving, problem, refusal, save, discard }
}

/**
 * A page's body while its settings are read, or when they could not be: the
 * failure in words with *Try again*, else a skeleton of the page's sections
 * (`heights`, one card each). The page draws itself once its draft is ready.
 */
export function AreaWaiting({ settings, label, heights = ['h-72'] }: { settings: SettingsArea<unknown>['settings']; label: string; heights?: string[] }) {
  if (settings.error && !settings.data) {
    return (
      <Alert tone="error">
        Could not load the settings: {settings.error}{' '}
        <Button variant="link" onClick={() => void settings.reload()}>
          Try again
        </Button>
      </Alert>
    )
  }
  return (
    <SkeletonRegion label={label} className="space-y-4">
      {heights.map((height, i) => (
        <Skeleton key={i} className={`${height} w-full rounded-2xl`} />
      ))}
    </SkeletonRegion>
  )
}
